import { IOSimPlusIntegrationService } from 'app/services/iosim-plus-integration/iosim-plus-integration.service';
import { RealTimeSimulationStatusProgress, SimulationStatusEnum } from '@shared/iosim-plus/models/simulationStatus';
import { FeatureToggle, RoleTypeEnum } from '../../../shared/enums';
import { Injectable } from '@angular/core';
import { GlobalSettingsService } from '@core/globalSettings.service';
import { TimberService } from '@logging/timber.service';
import { filter, take, takeUntil, tap } from 'rxjs/operators';
import { IOSimSimulationStatusProgressService } from '../../iosim-simulation.store/iosim-simulation-status-progress.service';
import { EupRoutesService } from '@core/eupRoutes.service';
import { BaseSignalR } from '../signalR/base-signalR';
import { IOSimSimulationInfo } from '../../iosim-simulation.store/iosim-simulation-status-progress-state.model';
import { ShellContextService } from 'app/services/shell-context/shell-context.service';
import { firstValueFrom } from 'rxjs';
import { FeaturesToggleSettingsService } from 'app/featuresToggleSettings/service/featuresToggleSettings.service';

@Injectable({ providedIn: 'root' })
export class RealTimeUpdateSimulationService extends BaseSignalR<RealTimeSimulationStatusProgress> {
	private readonly channelName = 'SimulationMessageReceived';
	private readonly hubName = 'iosimPlusNotificationHub';
	constructor(
		private eupRoutesService: EupRoutesService,
		private globalSettingsService: GlobalSettingsService,
		private simulationStatusProgressService: IOSimSimulationStatusProgressService,
		private integrationService: IOSimPlusIntegrationService,
		private shellContextService: ShellContextService,
		private featuresToggleSettingsService: FeaturesToggleSettingsService,
		timberService: TimberService
	) {
		super(timberService);
	}

	checkPermissionsAndStartRealUpdateSimulation() {
		this.integrationService.isIOSimPlusAllowed$
			.pipe(
				filter((isIOSimPlusAllowed) => isIOSimPlusAllowed && this.globalSettingsService.get().roleType === RoleTypeEnum.Doctor),
				tap(async (_) => {
					try {
						await this.startNotifications();
					} catch (error) {
						this.timberService.error(error, { module: 'RealTimeUpdateSimulationService' });
					}
				}),
				take(1),
				takeUntil(this.stopNotifications$)
			)
			.subscribe();
	}

	private async startNotifications(): Promise<void> {
		const notificationAgentUrl =  this.eupRoutesService.notificationAgentUrl;
		if (!notificationAgentUrl) {
			throw new Error('notificationAgentUrl url is not specified in global settings');
		}

		const useNewToken = this.featuresToggleSettingsService.isVisibleFeature(
			FeatureToggle.UseNewTokenInNotification,
			false
		);

		const hubUrl = `${notificationAgentUrl}${this.hubName}`;

		if (useNewToken) {
			const context = await firstValueFrom(
				this.shellContextService.getContext().pipe(
					filter((ctx) => !!ctx?.bizCtx?.contactId)
				)
			);
			if (!context) {
				throw new Error('Shell context is not available');
			}

			const { bizCtx } = context;
			const clientId = bizCtx?.contactId;
			const companies = this.globalSettingsService.get()?.companies?.map(c => c.id) || [];

			if (!clientId || !companies.length) {
				throw new Error('Missing required context properties for SignalR connection');
			}

			const queryParams = { companies: companies.join(','), clientId };
			await this.initMessages(
				hubUrl,
				this.channelName,
				() => firstValueFrom(this.shellContextService.getContext()).then(ctx => ctx?.security?.accessToken ?? ''),
				queryParams
			);
		} else {
			const token = this.globalSettingsService.get()?.jwtAccessToken;
			if (!token) {
				throw new Error('jwtAccessToken is not available in global settings');
			}
			await this.initMessages(hubUrl, this.channelName, () => this.globalSettingsService.get()?.jwtAccessToken ?? '');
		}

		this.messageBroker$
			.pipe(
				filter((simulationStatusProgress) => simulationStatusProgress.simulationStatus !== SimulationStatusEnum.NotStarted),
				tap((simulationStatusProgress) => this.addOrUpdateSimulationStatusProgress(simulationStatusProgress)),
				takeUntil(this.stopNotifications$)
			)
			.subscribe();
	}

	private addOrUpdateSimulationStatusProgress(simulationStatusProgress: RealTimeSimulationStatusProgress) {
		const simulationStatus: IOSimSimulationInfo = {
			orderId: simulationStatusProgress.orderId.toString(),
			progress: {
				simulationStatus: simulationStatusProgress.simulationStatus,
				startSimulationTime: simulationStatusProgress.startSimulationUTCTime,
				expectedDuration: simulationStatusProgress.expectedDurationInSeconds,
			},
		};

		this.simulationStatusProgressService.addOrUpdateSimulationStatusProgress(simulationStatus);
	}
}
